import { useMutation, useQuery } from '@apollo/client';

import keyBy from 'lodash/keyBy';
import noop from 'lodash/noop';

import { useMemo } from 'react';

import {
  CREATE_DEFAULT_WORKSPACE,
  CREATE_WORKSPACE_QUERY,
  DELETE_WORKSPACE_USER_QUERY,
  FETCH_DEFAULT_WORKSPACE,
  FETCH_WORKSPACES_QUERY,
  FETCH_WORKSPACE_BY_ID_QUERY,
  INSERT_WORKSPACE_USER_QUERY,
  UPDATE_WORKSPACE_USER_QUERY,
  transformWorkspaceResponse,
} from '../workspace_server';

import { modelDetailTimeRangePollInterval } from '../../config';

import { UserType } from '../../constants/user.constants';
import { augmentWorkspacesWithUsers } from '../../components/workspaces/workspace-utils';
import useUserContext from '../../components/contexts/UserData/UserData';

import keycloakService from '../../keycloak_service';
// import { components } from '../../../types/api';
// This request has experienced race conditions in the past which locked the dashboard in an error state
// If this happens in the future, ensure the poll interval is small enough to send a second request in a timely manner
export const useWorkspaceInfoById = workspaceId => {
  const { users = [], usersLoading, usersError } = useUserContext();
  const userDataMap = users ? keyBy(users, user => user.id) : {};
  const authHeader = keycloakService.getAuthHeader();

  const fetchWorkspaceQueryResult = useQuery(FETCH_WORKSPACE_BY_ID_QUERY, {
    skip: !workspaceId || !authHeader,
    variables: {
      id: workspaceId,
    },
    fetchPolicy: 'cache-and-network',
    pollInterval: modelDetailTimeRangePollInterval,
  });

  const workspaceResponse = fetchWorkspaceQueryResult?.data?.workspace_by_pk;
  const workspaceData = workspaceResponse ? transformWorkspaceResponse(workspaceResponse) : null;

  const workspaceUsers = workspaceData?.users || [];
  const usersWithType =
    workspaceUsers?.map(workspaceUser => ({
      ...workspaceUser,
      ...userDataMap[workspaceUser.userId],
    })) || [];

  const workspaceOwnerData = userDataMap[workspaceData?.createdBy];
  const workspace = useMemo(() => {
    if (!workspaceData) {
      return null;
    }
    return {
      ...workspaceData,
      owner: workspaceOwnerData,
    };
  }, [workspaceData, workspaceOwnerData]);
  return {
    workspace,
    workspaceUsers: usersWithType,
    loading: fetchWorkspaceQueryResult?.loading || usersLoading,
    error: fetchWorkspaceQueryResult?.error || usersError,
  };
};

export const useDefaultWorkspaceForUser = (userId, userEmail) => {
  const fetchWorkspaceQueryResult = useQuery(FETCH_DEFAULT_WORKSPACE, {
    skip: !userId || !userId?.length,
    variables: {
      user_id: userId,
    },
    fetchPolicy: 'cache-and-network',
    pollInterval: modelDetailTimeRangePollInterval,
  });

  const [CREATE_WORKSPACE_MUTATION] = useMutation(CREATE_DEFAULT_WORKSPACE, {
    variables: {
      user_id: userId,
      workspace_name: `${userEmail ?? 'admin'} - Default Workspace`,
      user_type: UserType.OWNER,
    },
    refetchQueries: [
      {
        query: FETCH_DEFAULT_WORKSPACE,
        variables: {
          user_id: userId,
        },
      },
    ],
  });

  const workspace = fetchWorkspaceQueryResult?.data?.user_default_workspace[0]?.workspace;

  if (
    !workspace &&
    !fetchWorkspaceQueryResult?.loading &&
    fetchWorkspaceQueryResult?.called &&
    userId
  ) {
    // We suppress errors from conflicts on updates due to nested upsert caveats in Hasura.
    // The refetchQueries above prevents us from needing to manually return to result of the mutation.
    // https://hasura.io/docs/1.0/graphql/core/mutations/upsert.html#nested-upsert-caveats
    CREATE_WORKSPACE_MUTATION().then(noop).catch(noop);
  }

  return {
    workspace: workspace ? transformWorkspaceResponse(workspace) : null,
    loading: fetchWorkspaceQueryResult?.loading,
    error: fetchWorkspaceQueryResult?.error,
    called: fetchWorkspaceQueryResult?.called,
  };
};

export const useWorkspacesForUser = userId => {
  const fetchWorkspacesQueryResult = useQuery(FETCH_WORKSPACES_QUERY, {
    skip: !userId || !userId?.length,
    variables: {
      user_id: userId,
    },
    fetchPolicy: 'cache-and-network',
    pollInterval: modelDetailTimeRangePollInterval,
  });

  const [addWorkspace] = useMutation(CREATE_WORKSPACE_QUERY);
  const handleAddWorkspace = workspaceName =>
    // We cannot provide an optimistic result; only an id is returned from the query.
    addWorkspace({
      variables: { user_id: userId, workspace_name: workspaceName, user_type: 'owner' },
    });

  const [addUserToWorkspace] = useMutation(INSERT_WORKSPACE_USER_QUERY);
  const handleAddUserToWorkspace = (userIdToAdd, userType, workspaceId) => {
    addUserToWorkspace({
      variables: {
        user_id: userIdToAdd,
        user_type: userType,
        workspace_id: workspaceId,
      },
      optimisticResponse: {
        insert_add_user_to_workspace_one: {
          id: userIdToAdd,
          user_id: userIdToAdd,
          user_type: userType,
          workspace_id: workspaceId,
        },
      },
    });
  };

  const [updateUserRole] = useMutation(UPDATE_WORKSPACE_USER_QUERY);
  const handleUpdateUserRole = (userIdToUpdate, workspaceId, userType) => {
    updateUserRole({
      variables: {
        workspace_id: workspaceId,
        user_id: userIdToUpdate,
        user_type: userType,
      },
    });
  };

  const [removeUserFromWorkspace] = useMutation(DELETE_WORKSPACE_USER_QUERY);
  const handleRemoveUserFromWorkspace = (userIdToRemove, workspaceId) => {
    removeUserFromWorkspace({
      variables: {
        user_id: userIdToRemove,
        workspace_id: workspaceId,
      },
    });
  };

  const userWorkspaces = useMemo(() => {
    const rawWorkspaceData = fetchWorkspacesQueryResult?.data?.workspace || [];
    if (!rawWorkspaceData?.length) {
      return [];
    }
    return rawWorkspaceData.map(transformWorkspaceResponse) || [];
  }, [fetchWorkspacesQueryResult]);

  const { users, usersLoading, usersError } = useUserContext();

  const augmentedWorkspaceData = useMemo(() => {
    if (!users?.length) {
      return userWorkspaces;
    }
    return augmentWorkspacesWithUsers(userWorkspaces, users);
  }, [userWorkspaces, users]);

  return {
    userWorkspaces: augmentedWorkspaceData,
    loading: fetchWorkspacesQueryResult?.loading || usersLoading,
    error: fetchWorkspacesQueryResult?.error || usersError,
    addWorkspace: handleAddWorkspace,
    addUserToWorkspace: handleAddUserToWorkspace,
    updateUserRole: handleUpdateUserRole,
    removeUserFromWorkspace: handleRemoveUserFromWorkspace,
  };
};

export default useWorkspacesForUser;
